import React from 'react';
import styles from './SiderResizeHandle.module.less';

export const DEFAULT_SIDER_WIDTH = 180;
export const MIN_SIDER_WIDTH = 180;
export const MAX_SIDER_WIDTH = 260;
export const SIDER_WIDTH_STORAGE_KEY = 'easy-record:sider-width:v2';
const COLLAPSED_THRESHOLD = 90;
const DEFAULT_HANDLE_TOP = 112;
const HITBOX_WIDTH = 12;

export const clampSiderWidth = (width: number) =>
  Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, Math.round(width)));

export const getStoredSiderWidth = () => {
  if (typeof window === 'undefined') return DEFAULT_SIDER_WIDTH;
  const stored = Number(window.localStorage.getItem(SIDER_WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0
    ? clampSiderWidth(stored)
    : DEFAULT_SIDER_WIDTH;
};

export const persistSiderWidth = (width: number) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(
      SIDER_WIDTH_STORAGE_KEY,
      String(clampSiderWidth(width)),
    );
  }
};

export type SiderResizeHandleProps = {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
};

const SiderResizeHandle: React.FC<SiderResizeHandleProps> = ({
  enabled,
  siderWidth,
  onResizeWidth,
}) => {
  const [dragging, setDragging] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  const [boundaryX, setBoundaryX] = React.useState(siderWidth);
  const [handleTop, setHandleTop] = React.useState(DEFAULT_HANDLE_TOP);
  const pointerIdRef = React.useRef<number | null>(null);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(siderWidth);
  const frameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const findSider = () =>
      document.querySelector<HTMLElement>('.ant-pro-sider, .ant-layout-sider');
    const updatePosition = () => {
      const sider = findSider();
      if (!sider) return;
      const rect = sider.getBoundingClientRect();
      if (rect.width <= COLLAPSED_THRESHOLD) {
        setBoundaryX(-1);
        return;
      }
      setBoundaryX(rect.right);
      const button = document.querySelector<HTMLElement>(
        '.ant-pro-sider-collapsed-button, .ant-layout-sider-trigger, [class*="collapse-button"]',
      );
      setHandleTop(
        Math.max(
          DEFAULT_HANDLE_TOP,
          Math.ceil((button?.getBoundingClientRect().bottom ?? 0) + 12),
        ),
      );
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    const sider = findSider();
    const observer =
      sider && 'ResizeObserver' in window
        ? new ResizeObserver(updatePosition)
        : undefined;
    if (observer && sider) observer.observe(sider);
    return () => {
      window.removeEventListener('resize', updatePosition);
      observer?.disconnect();
    };
  }, [enabled, siderWidth]);

  React.useEffect(() => {
    if (!dragging || typeof window === 'undefined') return undefined;
    const applyWidth = (width: number) => {
      if (frameRef.current !== null)
        window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        const nextWidth = clampSiderWidth(width);
        persistSiderWidth(nextWidth);
        onResizeWidth(nextWidth);
      });
    };
    const move = (event: PointerEvent) => {
      if (
        pointerIdRef.current !== null &&
        event.pointerId !== pointerIdRef.current
      )
        return;
      applyWidth(startWidthRef.current + event.clientX - startXRef.current);
    };
    const stop = () => {
      pointerIdRef.current = null;
      if (frameRef.current !== null)
        window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragging, onResizeWidth]);

  if (!enabled || boundaryX <= 0) return null;

  const style = {
    '--zy-handle-left': `${boundaryX - HITBOX_WIDTH / 2}px`,
    '--zy-handle-top': `${handleTop}px`,
    '--zy-handle-width': `${HITBOX_WIDTH}px`,
    '--zy-handle-offset': `${handleTop}px`,
  } as React.CSSProperties;

  return (
    <>
      {dragging ? <div className={styles.dragMask} /> : null}
      <div
        className={styles.hitBox}
        role="slider"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuemin={MIN_SIDER_WIDTH}
        aria-valuemax={MAX_SIDER_WIDTH}
        aria-valuenow={Math.round(siderWidth)}
        style={style}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          pointerIdRef.current = event.pointerId;
          startXRef.current = event.clientX;
          startWidthRef.current = siderWidth;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setDragging(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const step = event.shiftKey ? 24 : 8;
          const nextWidth = clampSiderWidth(
            siderWidth + (event.key === 'ArrowRight' ? step : -step),
          );
          persistSiderWidth(nextWidth);
          onResizeWidth(nextWidth);
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div
          className={[
            styles.handle,
            dragging ? styles.dragging : '',
            !dragging && hovering ? styles.hovering : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      </div>
    </>
  );
};

export default SiderResizeHandle;
